import React, { useState, useEffect } from "react";
import {
  Alert,
  Card,
  CardBody,
  Input,
  Label,
  Form,
  Button,
  Spinner,
} from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { forgetPassword, forgetPasswordReset } from "../../store/actions";
import AuthPageLayout from "../../../src/layouts/VerticalLayouts/AuthPageLayout";
import mailSendIcon from "../../assets/images/mail-send.svg";

const ForgetPasswordPage = () => {
  const { t } = useTranslation();
  document.title = t("common.pageTitle", {
    page: t("auth.forgotPassword.pageTitle"),
  });

  const dispatch = useDispatch();
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const { forgetError, forgetSuccess, loading } = useSelector((state) => ({
    forgetError: state.ForgetPassword.forgetError,
    forgetSuccess: state.ForgetPassword.forgetSuccess,
    loading: state.ForgetPassword.loading,
  }));

  const errorAlert = forgetError && (
    <Alert color="danger" className="mb-3">
      {t("auth.forgotPassword.sendFailed")}
    </Alert>
  );

  // Countdown timer for resend button
  useEffect(() => {
    if (forgetSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setCanResend(true);
    }
  }, [forgetSuccess, countdown]);

  // Reset form state and countdown when component unmounts
  useEffect(() => {
    return () => {
      dispatch(forgetPasswordReset());
      setCountdown(60);
      setCanResend(false);
    };
  }, [dispatch]);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      email: "",
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .required(t("auth.forgotPassword.emailRequired"))
        .matches(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          t("auth.validation.invalidEmail")
        ),
    }),
    onSubmit: (values) => {
      dispatch(forgetPassword(values));
    },
  });

  return (
    <AuthPageLayout>
      <Card className="mt-4">
        <CardBody
          style={{
            paddingTop: "28px",
            paddingBottom: "28px",
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        >
          {!forgetSuccess ? (
            <>
              {/* Initial state - Enter email */}
              <div className="mb-4">
                <h3 className="text-primary fw-semibold mb-2 auth-form-heading">
                  {t("auth.forgotPassword.heading")}
                </h3>
                <p className="text-muted mb-0 auth-form-subheading">
                  {t("auth.forgotPassword.subheading")}
                </p>
              </div>

              <Form onSubmit={validation.handleSubmit}>
                {errorAlert}
                <div className="mb-3">
                  <Label
                    htmlFor="email"
                    className="form-label fw-semibold auth-form-label"
                  >
                    {t("common.labels.email")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="email"
                    className="form-control auth-form-input"
                    placeholder={t("auth.fields.emailPlaceholder")}
                    type="email"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email || ""}
                    invalid={
                      validation.touched.email && validation.errors.email
                        ? true
                        : false
                    }
                  />
                  {validation.touched.email && validation.errors.email ? (
                    <div className="text-danger mt-1 auth-form-error">
                      {validation.errors.email}
                    </div>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Button
                    color="primary"
                    type="submit"
                    className="w-100 fw-semibold auth-form-button"
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner size="sm" />
                    ) : (
                      t("auth.forgotPassword.submit")
                    )}
                  </Button>
                </div>

                {/* Remember Password link */}
                <div className="text-center">
                  <span className="text-dark auth-form-footer-text">
                    {t("auth.links.rememberPassword")}
                  </span>
                  <Link
                    to="/login"
                    className="text-primary text-decoration-underline auth-form-footer-text"
                  >
                    {t("auth.actions.signIn")}
                  </Link>
                </div>
              </Form>
            </>
          ) : (
            <>
              {/* Success state - Check your email */}
              <div className="text-center mb-4">
                <div className="mt-3 mb-3 d-flex justify-content-center">
                  <img
                    src={mailSendIcon}
                    alt={t("auth.forgotPassword.mailSentAlt")}
                    style={{ width: "120px", height: "auto" }}
                  />
                </div>
                <h3 className="text-primary fw-semibold mb-2 auth-form-heading">
                  {t("auth.forgotPassword.checkEmail")}
                </h3>
                <p className="text-muted mb-0 auth-form-subheading">
                  {t("auth.forgotPassword.checkEmailText")}
                </p>
              </div>

              {errorAlert}
              <div className="mb-3">
                <Button
                  color="light"
                  className="w-100 fw-semibold auth-form-button"
                  disabled={!canResend || loading}
                  onClick={() => {
                    if (canResend && !loading) {
                      dispatch(
                        forgetPassword({ email: validation.values.email })
                      );
                      setCountdown(60);
                      setCanResend(false);
                    }
                  }}
                  style={{
                    backgroundColor: "#f5f5f5",
                    border: "none",
                    color: canResend ? "#212121" : "#bdbdbd",
                  }}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      {t("auth.forgotPassword.resend")}{" "}
                      {!canResend && `     ${countdown}s`}
                    </>
                  )}
                </Button>
              </div>

              {/* Remember Password link */}
              <div className="text-center">
                <span className="text-dark auth-form-footer-text">
                  {t("auth.links.rememberPassword")}
                </span>
                <Link
                  to="/login"
                  className="text-primary text-decoration-underline auth-form-footer-text"
                >
                  {t("auth.actions.signIn")}
                </Link>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </AuthPageLayout>
  );
};

export default withRouter(ForgetPasswordPage);
